import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { format } from 'date-fns';
import { Subject } from 'rxjs';
import { debounceTime, delayWhen, filter } from 'rxjs/operators';
import { Brand } from '~app/shared/enums/brand.enum';
import { Role } from '~app/shared/enums/role.enum';
import { environment } from '~env';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly scriptLoaded = new Subject();
  private readonly trackData = new Subject();
  private satellite: any;
  private readonly digitalData = {
    page: {
      pageInfo: {
        channel: 'pre-check',
        pageName: '',
        language: 'en-US',
      },
      attributes: {
        // bookingDesc: '',
        brand: '',
        country: '',
        // cruiseLength: '',
        // daysToCruise: '',
        departureDate: '',
        departurePort: '',
        // destination: '',
        // itinerary: '',
        platform: window.innerWidth > 720 ? 'web' : 'mobile',
        shipCode: '',
        // shipCodeDetected: '',
        shipDate: '',
        voyageID: '',
      },
    },
    user: {
      profile: {
        profileInfo: {
          profileID: '',
        },
      },
      attributes: {
        role: '',
        // department: '',
        // securityLevel: '',
        country: '',
        mode: '',
      },
    },
  };

  constructor(private readonly router: Router) {
    this.router.events
      .pipe(
        delayWhen(() => this.scriptLoaded),
        filter(event => event instanceof NavigationEnd),
      )
      .subscribe((ev: NavigationEnd) => {
        this.pageName = ev.url;
        this.track();
      });

    this.trackData
      .pipe(
        delayWhen(() => this.scriptLoaded),
        debounceTime(2000),
      )
      .subscribe(_ => {
        this.customEvent('page', this.digitalData);
      });
  }

  public start(): void {
    const el = window.document.createElement('script');
    el.type = 'text/javascript';
    el.async = true;
    el.src = environment.analytics.scriptSrc;
    el.onload = () => {
      this.satellite = (window as any)._satellite;
      this.satellite.pageBottom();
      this.scriptLoaded.next(true);
      this.scriptLoaded.complete();
    };
    el.onerror = () => {
      console.error('There was a problem loading the analytics script');
    };
    window.document.body.appendChild(el);
  }

  public set pageName(val: string) {
    if (val === '/') val = '/main';
    const pageName = val.replace(/\//g, ':');
    this.digitalData.page.pageInfo.pageName = `precheck${pageName}`;
    this.track();
  }

  public set brand(val: Brand) {
    this.digitalData.page.attributes.brand = val;
    this.track();
  }

  public set departureDate(val: Date) {
    this.digitalData.page.attributes.departureDate = format(val, 'yyyy-MM-dd');
    this.digitalData.page.attributes.shipDate = format(val, 'yyyy-MM-dd');
    this.updateVoyageID();
    this.track();
  }

  public set departurePort(val: string) {
    this.digitalData.page.attributes.departurePort = val;
    this.track();
  }

  public set shipCode(val: string) {
    this.digitalData.page.attributes.shipCode = val;
    this.updateVoyageID();
    this.track();
  }

  public set userId(val: string) {
    this.digitalData.user.profile.profileInfo.profileID = val;
    this.track();
  }

  public set userRole(val: Role) {
    if (val === Role.User) {
      this.digitalData.user.attributes.role = 'Crew';
    } else this.digitalData.user.attributes.role = val;
    this.track();
  }

  public set userCountry(val: string) {
    this.digitalData.user.attributes.country = val;
    this.track();
  }

  public set userMode(val: 'check-in' | 'non-active') {
    this.digitalData.user.attributes.mode = val;
    this.track();
  }

  public customEvent(analyticsEventName: string, data?: any): void {
    if (data) {
      this.dispatchEvent(analyticsEventName, data);
    } else {
      this.dispatchEvent(analyticsEventName, {});
    }
  }

  private dispatchEvent(analyticsEventName: string, data: any): void {
    const eventName = 'customAnalytics';
    const eventData = [analyticsEventName, data];
    let ev;
    if (typeof (window as any).CustomEvent === 'function') {
      ev = new CustomEvent(eventName, { detail: eventData });
    } else {
      ev = window.document.createEvent('CustomEvent');
      const event = new CustomEvent(eventName, { bubbles: true, cancelable: true, detail: eventData });
      window.document.dispatchEvent(event);
    }
    (window as any).digitalData = data;
    window.document.dispatchEvent(ev);
  }

  private track(): void {
    this.trackData.next(true);
  }

  private updateVoyageID(): void {
    if (this.digitalData.page.attributes.shipCode && this.digitalData.page.attributes.shipDate) {
      const date = this.digitalData.page.attributes.shipDate.replace(/-/g, '');
      this.digitalData.page.attributes.voyageID = `${this.digitalData.page.attributes.shipCode}${date}`;
    }
  }
}
